import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useNavigationPersona } from "../auth/navigationPersona";
import { usePermission } from "../auth/permissions/usePermission";
import { FEATURES } from "../features/featureRegistry";
import {
  DEFAULT_FEATURE_ICON,
  isFeatureVisibleInNavigation,
} from "../navigation/menuConfig";

import { Modal } from "./Modal";

import { canAny, getFeatureNavigationPermission } from "@/features/feature.types";

function normalize(text: string): string {
  return (text ?? "").toLowerCase().trim();
}

export function CommandPalette() {
  const navigate = useNavigate();
  const { can } = usePermission();
  const persona = useNavigationPersona();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const accessibleFeatures = useMemo(() => {
    return FEATURES.filter(
      (feature) =>
        canAny(can, getFeatureNavigationPermission(feature, persona)) &&
        isFeatureVisibleInNavigation(feature, persona),
    );
  }, [can, persona]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) {
      return accessibleFeatures;
    }

    return accessibleFeatures.filter((feature) => {
      const label = normalize(feature.navigation.label);
      const path = normalize(feature.navigation.path);
      const moduleName = normalize(feature.moduleName);
      return label.includes(q) || path.includes(q) || moduleName.includes(q);
    });
  }, [accessibleFeatures, query]);
  const selectedIndex = Math.min(activeIndex, Math.max(filtered.length - 1, 0));

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActiveIndex(0);
  }, []);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("sms:open-search", onOpen);
    return () => window.removeEventListener("sms:open-search", onOpen);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, filtered.length]);

  useEffect(() => {
    if (open)
      document
        .getElementById(`module-search-${selectedIndex}`)
        ?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex, open]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isK = e.key.toLowerCase() === "k";
      const modifier = e.ctrlKey || e.metaKey;

      if (modifier && isK) {
        e.preventDefault();
        setOpen((prev) => !prev);
        return;
      }

      if (!open) {
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  const goToFeature = useCallback(
    (index: number) => {
      const feature = filtered[index];
      if (!feature) {
        return;
      }
      close();
      navigate(feature.navigation.path);
    },
    [close, filtered, navigate],
  );

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      goToFeature(selectedIndex);
    }
  };

  return (
    <Modal isOpen={open} onClose={close} title="Search features" size="lg">
      <div className="space-y-4">
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
          </div>
          <input
            ref={inputRef}
            aria-label="Search available modules"
            role="combobox"
            aria-expanded={filtered.length > 0}
            aria-controls={filtered.length ? "module-search-results" : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              filtered.length ? `module-search-${selectedIndex}` : undefined
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Type to search…"
            className="block w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>

        <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-800">
          {filtered.length === 0 ? (
            <div className="p-4 text-sm text-slate-600 dark:text-slate-300">
              No matching features.
            </div>
          ) : (
            <ul
              id="module-search-results"
              role="listbox"
              aria-label="Available modules"
              className="divide-y divide-slate-200 dark:divide-slate-800"
            >
              {filtered.map((feature, idx) => {
                const active = idx === selectedIndex;
                const FeatureIcon = feature.navigation.icon ?? DEFAULT_FEATURE_ICON;
                return (
                  <li
                    key={feature.id}
                    id={`module-search-${idx}`}
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => goToFeature(idx)}
                    className={clsx(
                      "cursor-pointer px-4 py-3 text-sm",
                      active
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
                        : "bg-white text-slate-800 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800",
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <FeatureIcon className="h-5 w-5" />
                        <div className="text-left">
                          <div className="font-medium">{feature.navigation.label}</div>
                          <div className="caption">{feature.navigation.path}</div>
                        </div>
                      </div>
                      <div className="caption">{feature.moduleName}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
